import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Spinner from '../../../components/widgets/Spinner';
import ToggleTabGroup from '../../../components/atoms/ToggleTabGroup';
import OverflowingText from '../../../components/foundations/OverflowingText';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import { getAdminModeEnabled } from '../../../selectors/user';
import { CONTEXT_ENV_TOGGLE_OPTIONS } from '../../../chatView/constants';
import { ANOMALY_EVENTS, RCA_SOURCES, trackAnomalyEvent } from '../../consts';
import { getTopMovers } from '../../queryUtils';
import SunburstChart from './SunburstChart';
import SunburstEmptyState from './SunburstEmptyState';
import ChartLegend from './ChartLegend';
import TopMovers from './TopMovers';
import css from './Sunburst.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS';

const Sunburst = ({
    data,
    isLoading,
    error,
    hasQueried,
    chartColors,
    metricLabels,
    displayFormats,
    increasesAreGood,
    onRunRca,
    firstPeriod,
    secondPeriod,
    contextEnv,
    onContextEnvChange,
    activeBookmarkName,
    headerActions,
}) => {
    const hasData = data?.length > 0;
    const isAdmin = useSelector(getAdminModeEnabled);
    const topMovers = useMemo(() => getTopMovers(data ?? []), [data]);
    const [isMoversDismissed, setIsMoversDismissed] = useState(false);

    const handleMoversClose = () => {
        trackAnomalyEvent(ANOMALY_EVENTS.TOP_MOVERS_CLOSE, {});
        setIsMoversDismissed(true);
    };

    const handleMoversReopen = () => {
        trackAnomalyEvent(ANOMALY_EVENTS.TOP_MOVERS_OPEN, {});
        setIsMoversDismissed(false);
    };

    return (
        <div className={css.container}>
            <div className={css.headerWrapper}>
                <div>
                    <div className={css.title}>
                        <Translate id={`${TRANSLATION_PREFIX}.PAGE_TITLE`} />
                        {activeBookmarkName && (
                            <OverflowingText className={css.bookmarkName}>- {activeBookmarkName}</OverflowingText>
                        )}
                    </div>
                    <div className={css.subtitle}>
                        <Translate id={`${TRANSLATION_PREFIX}.PAGE_SUBTITLE`} />
                    </div>
                </div>
                <div className={css.headerActions}>
                    {headerActions}
                    {isAdmin && (
                        <ToggleTabGroup
                            options={CONTEXT_ENV_TOGGLE_OPTIONS}
                            selectedOption={contextEnv}
                            onToggle={onContextEnvChange}
                        />
                    )}
                </div>
            </div>
            <div className={css.chartCard}>
                {hasData && !isLoading && (
                    <div className={css.chartToolbar}>
                        <ChartLegend chartColors={chartColors} />
                        <div className={css.toolbarRow}>
                            <div className={css.rcaHint}>
                                <Icon name="sparkles" size={IconSizes.X_SMALL} interactive={false} />
                                <Translate id={`${TRANSLATION_PREFIX}.SUNBURST.RCA_HINT`} />
                            </div>
                            {isMoversDismissed && (
                                <div className={css.reopenMovers} onClick={handleMoversReopen}>
                                    <Icon name="sparkles" size={IconSizes.X_SMALL} interactive={false} />
                                    <Translate id={`${TRANSLATION_PREFIX}.TOP_MOVERS.TITLE`} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={css.chartBody}>
                    <div className={css.content}>
                        {isLoading ? (
                            <Spinner show />
                        ) : error ? (
                            <Translate id={`${TRANSLATION_PREFIX}.SUNBURST.ERROR_STATE`} />
                        ) : !hasData ? (
                            hasQueried ? (
                                <Translate id={`${TRANSLATION_PREFIX}.SUNBURST.NO_RESULTS`} />
                            ) : (
                                <SunburstEmptyState />
                            )
                        ) : (
                            <SunburstChart
                                data={data}
                                metricLabels={metricLabels}
                                displayFormats={displayFormats}
                                increasesAreGood={increasesAreGood}
                                onRunRca={onRunRca}
                                firstPeriod={firstPeriod}
                                secondPeriod={secondPeriod}
                            />
                        )}
                    </div>
                    {hasData && !isLoading && !error && !isMoversDismissed && (
                        <TopMovers
                            movers={topMovers}
                            increasesAreGood={increasesAreGood}
                            metricLabel={metricLabels?.color}
                            onInvestigate={point => onRunRca?.(point, RCA_SOURCES.TOP_MOVERS)}
                            onClose={handleMoversClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

Sunburst.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            parent: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
            custom: PropTypes.shape({
                colorP1: PropTypes.number,
                colorP2: PropTypes.number,
                rawColorChange: PropTypes.number,
                rawColorChangePct: PropTypes.number,
                sizeP1: PropTypes.number,
                sizeP2: PropTypes.number,
                rawSizeChange: PropTypes.number,
                rawSizeChangePct: PropTypes.number,
                ringLevel: PropTypes.number,
                originalColor: PropTypes.string,
            }),
        })
    ),
    isLoading: PropTypes.bool,
    error: PropTypes.bool,
    hasQueried: PropTypes.bool,
    chartColors: PropTypes.shape({
        positive: PropTypes.string,
        negative: PropTypes.string,
        neutral: PropTypes.string,
        noData: PropTypes.string,
    }),
    metricLabels: PropTypes.shape({
        color: PropTypes.string,
        size: PropTypes.string,
    }),
    displayFormats: PropTypes.shape({
        color: PropTypes.string,
        size: PropTypes.string,
    }),
    increasesAreGood: PropTypes.bool,
    onRunRca: PropTypes.func,
    firstPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    secondPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    contextEnv: PropTypes.string,
    onContextEnvChange: PropTypes.func,
    activeBookmarkName: PropTypes.string,
    headerActions: PropTypes.node,
};

export default Sunburst;
